<template>
  <div>
    <div
      ref="toolboxWidget"
      class="smart-toolbox"
      :class="[
        { 'is-dragging': dragging, 'is-docked': !!dock, 'is-open': panelOpen && !dock, 'is-intro': introVisible },
        dock ? `dock-${dock}` : '',
      ]"
      :style="containerStyle"
    >
      <div
        class="toolbox-ball"
        :class="{ 'ball-hint-pulse': introVisible }"
        :title="dock ? $tt('点击展开智能工具箱') : $tt('智能工具箱，可拖到边缘吸附')"
        @mousedown="onDragStart"
        @touchstart.prevent="onDragStart"
        @click.stop="onBallClick"
      >
        <template v-if="dock">
          <i class="dock-chevron" :class="'chevron-' + dock" />
        </template>
        <template v-else>
          <i class="el-icon-s-operation" />
        </template>
      </div>
      <div
        v-show="panelOpen && !dock"
        class="toolbox-panel"
        :class="panelPlacementClass"
        @mousedown.stop
        @touchstart.stop
      >
        <div class="panel-title">{{ $tt("智能工具箱") }}</div>
        <div class="panel-grid">
          <div
            v-for="(cell, idx) in gridCells"
            :key="cell.id || 'empty-' + idx"
            class="grid-cell"
            :class="{ disabled: !cell.enabled, filled: cell.enabled }"
            :title="$tt(cell.desc || '即将开放')"
            @click="onCellClick(cell)"
          >
            <span class="cell-icon" :style="{ background: cell.color || '#e4e7ed' }">
              <i :class="cell.icon || 'el-icon-more'" />
            </span>
            <span class="cell-name">{{ $tt(cell.name || "敬请期待") }}</span>
          </div>
        </div>
      </div>
      <transition name="toolbox-intro-fade">
        <div
          v-if="introVisible"
          class="toolbox-intro-bubble"
          :class="introBubbleClass"
          @mousedown.stop
          @touchstart.stop
        >
          <div class="intro-avatar">
            <i class="el-icon-s-operation" />
          </div>
          <div class="intro-body">
            <p class="intro-title">{{ $tt("嗨，我是智能工具箱～") }}</p>
            <p class="intro-text">{{ $tt("点击我查看我有什么功能吧") }}</p>
            <el-button type="primary" size="mini" @click="dismissIntro">{{ $tt("知道了") }}</el-button>
          </div>
          <span class="intro-tail" />
        </div>
      </transition>
    </div>
    <ButtonToImageDialog :visible.sync="buttonDialogVisible" />
  </div>
</template>

<script>
import { TOOLBOX_TOOLS, TOOLBOX_GRID_SIZE } from "./tools";
import ButtonToImageDialog from "./ButtonToImageDialog.vue";

const STORAGE_KEY = "smartToolboxWidgetPos";
const INTRO_STORAGE_KEY = "cloud_smart_toolbox_intro_v1";
const EDGE_SNAP = 28;
const DOCK_PEEK = 14;

export default {
  components: { ButtonToImageDialog },
  props: {
    aiEnabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      panelOpen: false,
      buttonDialogVisible: false,
      introVisible: false,
      introTimer: null,
      pos: { left: null, top: null },
      dock: null,
      dragging: false,
      dragMoved: false,
      dragOffset: { x: 0, y: 0 },
      dragStartClient: { x: 0, y: 0 },
    };
  },
  computed: {
    gridCells() {
      const cells = TOOLBOX_TOOLS.slice();
      cells.splice(1, 0, {
        id: "canvasAi",
        name: "AI助手",
        desc: this.aiEnabled ? "打开/收起画布 AI（Alt+3）" : "当前账号未开通画布AI",
        icon: "el-icon-chat-dot-round",
        color: "#67c23a",
        enabled: !!this.aiEnabled,
      });
      while (cells.length < TOOLBOX_GRID_SIZE) {
        cells.push({
          id: "",
          name: "敬请期待",
          desc: "更多智能小工具即将上线",
          icon: "el-icon-plus",
          enabled: false,
        });
      }
      return cells.slice(0, TOOLBOX_GRID_SIZE);
    },
    containerStyle() {
      if (this.dock) {
        return this.dockedStyle;
      }
      const left =
        this.pos.left != null ? this.pos.left : Math.max(0, window.innerWidth - 56);
      const top =
        this.pos.top != null ? this.pos.top : Math.max(80, window.innerHeight / 2 - 24);
      return {
        left: `${left}px`,
        top: `${top}px`,
        right: "auto",
        bottom: "auto",
      };
    },
    dockedStyle() {
      const base = { right: "auto", bottom: "auto", left: "auto", top: "auto" };
      if (this.dock === "left") {
        return { ...base, left: "0", top: `${this.clampDockAlong("y")}px` };
      }
      if (this.dock === "right") {
        return { ...base, right: "0", top: `${this.clampDockAlong("y")}px` };
      }
      if (this.dock === "top") {
        return { ...base, top: "0", left: `${this.clampDockAlong("x")}px` };
      }
      return { ...base, bottom: "0", left: `${this.clampDockAlong("x")}px` };
    },
    panelPlacementClass() {
      if (this.dock === "left") return "panel-right";
      if (this.dock === "right") return "panel-left";
      const left = this.pos.left != null ? this.pos.left : window.innerWidth;
      return left > window.innerWidth / 2 ? "panel-left" : "panel-right";
    },
    introBubbleClass() {
      if (this.dock === "left") return "bubble-right";
      if (this.dock === "right") return "bubble-left";
      if (this.dock === "top") return "bubble-bottom";
      if (this.dock === "bottom") return "bubble-top";
      const left = this.pos.left != null ? this.pos.left : window.innerWidth;
      return left > window.innerWidth / 2 ? "bubble-left" : "bubble-right";
    },
  },
  mounted() {
    this.restorePos();
    window.addEventListener("resize", this.onResize);
    this.scheduleIntroHint();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
    this.unbindDrag();
    if (this.introTimer) {
      clearTimeout(this.introTimer);
      this.introTimer = null;
    }
  },
  methods: {
    hasSeenIntro() {
      try {
        return localStorage.getItem(INTRO_STORAGE_KEY) === "1";
      } catch (e) {
        return false;
      }
    },
    markIntroSeen() {
      try {
        localStorage.setItem(INTRO_STORAGE_KEY, "1");
      } catch (e) {
        /* ignore */
      }
    },
    scheduleIntroHint() {
      if (this.hasSeenIntro()) {
        return;
      }
      this.introTimer = setTimeout(() => {
        this.introTimer = null;
        if (this.hasSeenIntro()) {
          return;
        }
        this.introVisible = true;
      }, 900);
    },
    dismissIntro() {
      this.introVisible = false;
      this.markIntroSeen();
    },
    clampDockAlong(axis) {
      if (axis === "y") {
        const t = this.pos.top != null ? this.pos.top : window.innerHeight / 2;
        return Math.min(Math.max(56, t), window.innerHeight - 48);
      }
      const l = this.pos.left != null ? this.pos.left : window.innerWidth / 2;
      return Math.min(Math.max(8, l), window.innerWidth - 48);
    },
    restorePos() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) {
          this.pos = {
            left: window.innerWidth - 56,
            top: Math.max(80, window.innerHeight / 2 - 24),
          };
          this.dock = "right";
          return;
        }
        const saved = JSON.parse(raw);
        this.pos = {
          left: typeof saved.left === "number" ? saved.left : window.innerWidth - 56,
          top: typeof saved.top === "number" ? saved.top : window.innerHeight / 2,
        };
        this.dock = saved.dock || "right";
        this.clampPos();
      } catch (e) {
        this.pos = {
          left: window.innerWidth - 56,
          top: window.innerHeight / 2,
        };
        this.dock = "right";
      }
    },
    persistPos() {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          left: this.pos.left,
          top: this.pos.top,
          dock: this.dock,
        })
      );
    },
    clampPos() {
      const size = this.dock ? DOCK_PEEK + 8 : 48;
      const maxL = Math.max(0, window.innerWidth - size);
      const maxT = Math.max(56, window.innerHeight - size);
      if (this.pos.left != null) {
        this.pos.left = Math.min(Math.max(0, this.pos.left), maxL);
      }
      if (this.pos.top != null) {
        this.pos.top = Math.min(Math.max(56, this.pos.top), maxT);
      }
    },
    onResize() {
      this.clampPos();
      this.persistPos();
    },
    onBallClick() {
      if (this.dragMoved) {
        return;
      }
      if (this.introVisible) {
        this.dismissIntro();
      }
      if (this.dock) {
        this.dock = null;
        this.panelOpen = true;
        this.persistPos();
        return;
      }
      if (this.panelOpen) {
        this.panelOpen = false;
        this.applyEdgeSnap();
        this.persistPos();
        return;
      }
      this.panelOpen = true;
    },
    onCellClick(cell) {
      if (!cell || !cell.enabled) {
        return;
      }
      if (cell.id === "buttonToImage") {
        this.buttonDialogVisible = true;
        this.panelOpen = false;
        return;
      }
      if (cell.id === "canvasAi") {
        this.$emit("open-ai");
        this.panelOpen = false;
      }
    },
    onDragStart(e) {
      if (this.introVisible) {
        this.dismissIntro();
      }
      const point = e.touches ? e.touches[0] : e;
      this.dragging = true;
      this.dragMoved = false;
      this.dragStartClient = { x: point.clientX, y: point.clientY };
      const rect = this.$el.querySelector(".toolbox-ball").getBoundingClientRect();
      this.dragOffset = {
        x: point.clientX - rect.left,
        y: point.clientY - rect.top,
      };
      this.bindDrag();
    },
    onDragMove(e) {
      if (!this.dragging) {
        return;
      }
      const point = e.touches ? e.touches[0] : e;
      if (e.cancelable) {
        e.preventDefault();
      }
      const dx = point.clientX - this.dragStartClient.x;
      const dy = point.clientY - this.dragStartClient.y;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
        this.dragMoved = true;
        this.panelOpen = false;
        this.dock = null;
      }
      this.pos = {
        left: point.clientX - this.dragOffset.x,
        top: point.clientY - this.dragOffset.y,
      };
      this.clampPos();
    },
    onDragEnd() {
      if (!this.dragging) {
        return;
      }
      const moved = this.dragMoved;
      this.dragging = false;
      this.unbindDrag();
      if (moved) {
        this.applyEdgeSnap();
        this.panelOpen = false;
        this.persistPos();
      }
      window.setTimeout(() => {
        this.dragMoved = false;
      }, 0);
    },
    applyEdgeSnap() {
      const size = 48;
      const l = this.pos.left || 0;
      const t = this.pos.top || 0;
      const r = window.innerWidth - (l + size);
      const b = window.innerHeight - (t + size);
      const dists = [
        { side: "left", d: l },
        { side: "right", d: r },
        { side: "top", d: t },
        { side: "bottom", d: b },
      ];
      dists.sort((a, c) => a.d - c.d);
      if (dists[0].d <= EDGE_SNAP) {
        this.dock = dists[0].side;
        this.panelOpen = false;
      } else {
        this.dock = null;
      }
    },
    bindDrag() {
      document.addEventListener("mousemove", this.onDragMove);
      document.addEventListener("mouseup", this.onDragEnd);
      document.addEventListener("touchmove", this.onDragMove, { passive: false });
      document.addEventListener("touchend", this.onDragEnd);
    },
    unbindDrag() {
      document.removeEventListener("mousemove", this.onDragMove);
      document.removeEventListener("mouseup", this.onDragEnd);
      document.removeEventListener("touchmove", this.onDragMove);
      document.removeEventListener("touchend", this.onDragEnd);
    },
  },
};
</script>

<style lang="scss" scoped>
.smart-toolbox {
  position: fixed;
  z-index: 3200;
  user-select: none;
  touch-action: none;
  overflow: visible;
  &.is-intro {
    z-index: 10001;
  }
  &.is-dragging {
    z-index: 10002;
    opacity: 0.92;
  }
  &.is-open {
    z-index: 3201;
  }
}
.toolbox-ball {
  position: relative;
  z-index: 2;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #409eff, #66b1ff);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(64, 158, 255, 0.45);
  cursor: grab;
  font-size: 20px;
  &:active {
    cursor: grabbing;
  }
  &.ball-hint-pulse {
    animation: toolbox-ball-pulse 1.2s ease-in-out infinite;
  }
}
@keyframes toolbox-ball-pulse {
  0%,
  100% {
    box-shadow: 0 4px 14px rgba(64, 158, 255, 0.45);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(64, 158, 255, 0.22), 0 6px 18px rgba(64, 158, 255, 0.55);
    transform: scale(1.06);
  }
}
.toolbox-intro-fade-enter-active,
.toolbox-intro-fade-leave-active {
  transition: opacity 0.28s ease, transform 0.28s ease;
}
.toolbox-intro-fade-enter,
.toolbox-intro-fade-leave-to {
  opacity: 0;
  transform: translateY(6px) scale(0.96);
}
.toolbox-intro-bubble {
  position: absolute;
  z-index: 1;
  width: 248px;
  padding: 12px 14px 12px 12px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 28px rgba(64, 158, 255, 0.22), 0 2px 10px rgba(0, 0, 0, 0.08);
  border: 1px solid #d9ecff;
  display: flex;
  gap: 10px;
  text-align: left;
  pointer-events: auto;
  &.bubble-left {
    right: calc(100% + 14px);
    top: 50%;
    transform: translateY(-50%);
    .intro-tail {
      right: -7px;
      top: 50%;
      margin-top: -7px;
      border-width: 7px 0 7px 8px;
      border-color: transparent transparent transparent #fff;
      filter: drop-shadow(2px 0 1px rgba(64, 158, 255, 0.12));
    }
  }
  &.bubble-right {
    left: calc(100% + 14px);
    top: 50%;
    transform: translateY(-50%);
    .intro-tail {
      left: -7px;
      top: 50%;
      margin-top: -7px;
      border-width: 7px 8px 7px 0;
      border-color: transparent #fff transparent transparent;
      filter: drop-shadow(-2px 0 1px rgba(64, 158, 255, 0.12));
    }
  }
  &.bubble-top {
    left: 50%;
    bottom: calc(100% + 14px);
    transform: translateX(-50%);
    .intro-tail {
      left: 50%;
      bottom: -7px;
      margin-left: -7px;
      border-width: 8px 7px 0 7px;
      border-color: #fff transparent transparent transparent;
    }
  }
  &.bubble-bottom {
    left: 50%;
    top: calc(100% + 14px);
    transform: translateX(-50%);
    .intro-tail {
      left: 50%;
      top: -7px;
      margin-left: -7px;
      border-width: 0 7px 8px 7px;
      border-color: transparent transparent #fff transparent;
    }
  }
}
.intro-avatar {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #409eff, #66b1ff);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}
.intro-body {
  flex: 1;
  min-width: 0;
}
.intro-title {
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  line-height: 1.35;
}
.intro-text {
  margin: 0 0 10px;
  font-size: 12px;
  color: #606266;
  line-height: 1.55;
}
.intro-tail {
  position: absolute;
  width: 0;
  height: 0;
  border-style: solid;
}
.is-docked .toolbox-ball {
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.18);
}
.dock-left .toolbox-ball {
  width: 14px;
  height: 44px;
  border-radius: 0 10px 10px 0;
}
.dock-right .toolbox-ball {
  width: 14px;
  height: 44px;
  border-radius: 10px 0 0 10px;
}
.dock-top .toolbox-ball {
  width: 44px;
  height: 14px;
  border-radius: 0 0 10px 10px;
}
.dock-bottom .toolbox-ball {
  width: 44px;
  height: 14px;
  border-radius: 10px 10px 0 0;
}
.dock-chevron {
  display: inline-block;
  width: 0;
  height: 0;
  border-style: solid;
  &.chevron-left {
    border-width: 5px 0 5px 6px;
    border-color: transparent transparent transparent #fff;
  }
  &.chevron-right {
    border-width: 5px 6px 5px 0;
    border-color: transparent #fff transparent transparent;
  }
  &.chevron-top {
    border-width: 6px 5px 0 5px;
    border-color: #fff transparent transparent transparent;
  }
  &.chevron-bottom {
    border-width: 0 5px 6px 5px;
    border-color: transparent transparent #fff transparent;
  }
}
.toolbox-panel {
  position: absolute;
  width: 320px;
  padding: 12px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  &.panel-left {
    right: 56px;
    top: 0;
  }
  &.panel-right {
    left: 56px;
    top: 0;
  }
}
.panel-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 10px;
  text-align: left;
}
.panel-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}
.grid-cell {
  height: 86px;
  border-radius: 8px;
  border: 1px solid #ebeef5;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  background: #fafafa;
  &.filled:hover {
    border-color: #409eff;
    background: #ecf5ff;
  }
  &.disabled {
    cursor: not-allowed;
    pointer-events: none;
    opacity: 0.42;
    filter: grayscale(1);
  }
}
.cell-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}
.cell-name {
  font-size: 12px;
  color: #606266;
  line-height: 1.2;
}
</style>
